import React from 'react';

import Avatar from '@mui/material/Avatar';
import type { SxProps, Theme } from '@mui/material/styles';

import { resolveEffectiveAvatarUrl } from '@/lib/api';
import { isLLM } from '@/lib/utils';
import { mergeSx } from '@/theme/mergeSx';

/** Defines the shared avatar rendering options for identity components. */
export interface UserAvatarProps {
  /** Identifies the user whose avatar is being rendered. */
  username: string;
  /** Supplies the raw avatar URL before normalization. */
  avatarUrl?: string | null;
  /** Sets the rendered avatar width and height. */
  size: number;
  /** Controls the fallback URL used when avatarUrl is missing. */
  fallbackUrl?: string | null;
  /** Overrides the generated avatar alt text when needed. */
  alt?: string;
  /** Applies optional MUI styling to the Avatar. */
  sx?: SxProps<Theme>;
}

/** Renders a user's avatar with AI suppression and consistent URL resolution. */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  username,
  avatarUrl,
  size,
  fallbackUrl = '/avatar-placeholder.svg',
  alt,
  sx,
}) => {
  const avatarSx = mergeSx({ width: size, height: size, flexShrink: 0 }, sx);

  if (isLLM(username)) {
    return null;
  }

  const effectiveUrl = avatarUrl ? resolveEffectiveAvatarUrl(avatarUrl) : fallbackUrl;
  if (!effectiveUrl) {
    return null;
  }

  return (
    <Avatar
      src={effectiveUrl}
      alt={alt ?? `${username} avatar`}
      data-testid={`user-avatar-${username}`}
      sx={avatarSx}
    />
  );
};
